import { Logger } from '@/utils/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { Button, TextField, toast, Typography } from '@montugroup/design-system';
import { Box, Grid2, styled } from '@mui/material';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import settings from '../../constants/constants';
import AdminPageLayout from '../layout/AdminPageLayout';

const logger = new Logger('resetPassword');
const url = settings.url;

enum PasswordErrorCode {
  PASSWORD_REUSE = 'PasswordReuseError',
  CURRENT_PASSWORD = 'CurrentPasswordError',
  REQUIREMENTS_NOT_MET = 'password.requirements_not_met',
}

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
}));

type ResetPasswordFormValues = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

const formSchema = z
  .object({
    currentPassword: z.string().min(1, { message: 'You must enter your current password' }),
    password: UserPasswordSchema,
    confirmPassword: z.string().min(1, { message: 'Please confirm your new password' }),
  })
  .superRefine((values, ctx) => {
    if (values.currentPassword === values.password) {
      ctx.addIssue({
        message: 'New password cannot be the same as current password',
        code: 'custom',
        path: ['password'],
      });
    }

    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        message: 'Passwords do not match',
        code: 'custom',
        path: ['confirmPassword'],
      });
    }
  });

function ResetPasswordForm() {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const [passwordLoading, setPasswordLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (formValues: ResetPasswordFormValues) => {
    const { currentPassword, password } = formValues;

    try {
      setPasswordLoading(true);
      const response = await axios.put(`${url}/user/password`, {
        // NOTE: This null fallback is just incase email is undefined because we need to send an email key on the body
        email: user?.email ?? null,
        currentPassword,
        password,
        body: JSON.stringify({ currentPassword, password }),
      });

      if (response.status === 200 && response.data.id === 1) {
        toast.success('Password details are updated');
        window.location.replace('/orders');
      } else {
        toast.error('Password details not updated');
        logger.error('Password update failed: response.data.id === 0');
        navigate('/password');
      }
    } catch (error: any) {
      if (error.response?.status === 400 && error.response?.data?.error_code) {
        switch (error.response.data.error_code) {
          case PasswordErrorCode.PASSWORD_REUSE:
            toast.error('This password has been used before. Please choose a different password.');
            break;
          case PasswordErrorCode.CURRENT_PASSWORD:
            toast.error('This is your current password. Please choose a different password.');
            break;
          default:
            toast.error(error.response.data.message || 'Invalid password. Please try again.');
        }
      } else if (error.response?.status === 403 && error.response?.data?.error?.code) {
        switch (error.response?.data?.error?.code) {
          case PasswordErrorCode.REQUIREMENTS_NOT_MET:
            toast.error(
              'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
            );
            break;
          default:
            toast.error(
              error.response.data.message || error.response.data.error.message || 'Invalid password. Please try again.',
            );
        }
      } else {
        toast.error('Something went wrong!');
      }
      logger.error('Password update error', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <AdminPageLayout title="Change your password" fullWidth={false} testId="reset-password">
      <Box component="form" data-private autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Typography mt={4} mb={6}>
          To update your password, please enter your new password below. Your password must:
          <PasswordRequirements />
        </Typography>
        <Grid2 container spacing={4} sx={(theme) => ({ marginTop: theme.spacing(1) })}>
          <Grid2 size={{ xs: 12 }}>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  fullWidth
                  label="Current Password"
                  error={!!errors.currentPassword?.message}
                  helperText={errors.currentPassword?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  fullWidth
                  label="New Password"
                  error={!!errors.password?.message}
                  helperText={errors.password?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="password"
                  fullWidth
                  label="Confirm Password"
                  error={!!errors.confirmPassword?.message}
                  helperText={errors.confirmPassword?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <ButtonContainer>
          <Button type="submit" variant="contained" disabled={passwordLoading}>
            Change Password
          </Button>
        </ButtonContainer>
      </Box>
    </AdminPageLayout>
  );
}

export default ResetPasswordForm;
